import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHomeData } from '@/hooks/useTranslatedData';

export default function CTASection() {
  const ctaData = useHomeData().cta;
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-brand-cream py-16 md:py-24">
      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Image Container */}
        <div
          className={`relative w-full rounded-3xl overflow-hidden mb-10 md:mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20food%20laboratory%20with%20scientist%20hands%20working%20on%20natural%20ingredients%2C%20warm%20natural%20lighting%2C%20clean%20minimalist%20workspace%2C%20glass%20beakers%20and%20petri%20dishes%20with%20botanical%20extracts%2C%20mint%20green%20and%20coral%20warm%20tones%2C%20professional%20editorial%20food%20science%20photography%2C%20film%20grain%20texture%2C%20artistic%20composition&width=1200&height=500&seq=20&orientation=landscape"
            alt={ctaData.descriptionAccent}
            className="w-full h-64 md:h-96 lg:h-[480px] object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-brand-charcoal/10" />
        </div>

        {/* Text Content */}
        <div className="text-center max-w-3xl mx-auto">
          <h2
            className={`text-2xl md:text-4xl lg:text-[42px] leading-snug mb-6 transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="font-bold text-brand-charcoal">{ctaData.title}</span>
            <br />
            <span className="font-serif italic text-brand-gray">{ctaData.titleAccent}</span>
          </h2>

          <p
            className={`text-sm md:text-base text-brand-gray mb-8 leading-relaxed transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {ctaData.description}
            <br />
            <span className="font-semibold text-brand-charcoal">{ctaData.descriptionAccent}</span>
            {ctaData.descriptionEnd}
          </p>

          <div
            className={`transition-all duration-700 delay-400 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-10 py-4 bg-brand-charcoal text-white text-sm font-semibold rounded-full hover:bg-brand-stone transition-colors whitespace-nowrap"
            >
              {ctaData.buttonText}
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}